import { create } from 'zustand'
import { apiClient } from '../api/client'
import { API_ENDPOINTS } from '../api/endpoints'
import { applyThemeColorOverrides, clearThemeColorOverrides } from '../utils/themeColorUtils'

export type ColorOverrides = Record<string, string>

interface ColorSettingsState {
  colorOverrides: ColorOverrides
  isLoading: boolean
  isSaving: boolean
  loadUserColorOverrides: (userId: string) => Promise<void>
  saveUserColorOverrides: (userId: string, overrides: ColorOverrides) => Promise<void>
  setColorOverrides: (overrides: ColorOverrides) => void
  clearLocalOverrides: () => void
}

const buildUserThemeKey = (userId: string) => `UserThemeColors.${userId}`

export const useColorSettingsStore = create<ColorSettingsState>((set) => ({
  colorOverrides: {},
  isLoading: false,
  isSaving: false,

  setColorOverrides: (overrides) => {
    set({ colorOverrides: overrides })
    applyThemeColorOverrides(overrides)
  },

  clearLocalOverrides: () => {
    set({ colorOverrides: {} })
    clearThemeColorOverrides()
  },

  loadUserColorOverrides: async (userId: string) => {
    if (!userId) return
    const key = buildUserThemeKey(userId)
    set({ isLoading: true })
    try {
      const response = await apiClient.get(API_ENDPOINTS.systemSettings.get(key))
      const rawValue = response.data?.settingValue || response.data?.SettingValue || ''
      const parsed = rawValue ? (JSON.parse(rawValue) as ColorOverrides) : {}
      set({ colorOverrides: parsed, isLoading: false })
      applyThemeColorOverrides(parsed)
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 404) {
        set({ colorOverrides: {}, isLoading: false })
        clearThemeColorOverrides()
        return
      }
      console.error('Error loading color overrides:', error)
      set({ colorOverrides: {}, isLoading: false })
    }
  },

  saveUserColorOverrides: async (userId: string, overrides: ColorOverrides) => {
    if (!userId) return
    const key = buildUserThemeKey(userId)
    set({ isSaving: true })
    try {
      const payload: Record<string, any> = {
        settingKey: key,
        settingValue: JSON.stringify(overrides),
        settingType: 'json',
        description: 'User theme colors',
        descriptionEn: 'User theme colors'
      }
      const numericUserId = Number(userId)
      if (!Number.isNaN(numericUserId)) {
        payload.User_ID = numericUserId
      }
      await apiClient.post(API_ENDPOINTS.systemSettings.base, payload)
      set({ colorOverrides: overrides, isSaving: false })
      applyThemeColorOverrides(overrides)
    } catch (error) {
      console.error('Error saving color overrides:', error)
      set({ isSaving: false })
      throw error
    }
  }
}))

